import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { ResponsiveScatterPlot } from '@nivo/scatterplot';
import * as reportService from '../services/reportService';

const PRIORITY_COLORS: Record<string, string> = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#3b82f6',
  low: '#10b981',
};

export function RequirementAgingReport(): React.ReactElement {
  const { token, user } = useAuth();
  const [reportData, setReportData] = useState<reportService.AgingReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [timescale, setTimescale] = useState<'week' | 'month' | 'quarter' | 'year' | 'all'>('all');

  const isTaskBoardUser = user?.role === 'taskboard_user';

  const TIMESCALE_DAYS: Record<string, number> = {
    week: 7,
    month: 30,
    quarter: 90,
    year: 365,
    all: Infinity,
  };

  useEffect(() => {
    async function loadReport() {
      if (!token) return;
      try {
        setLoading(true);
        setError(null);
        const data = await reportService.getRequirementAgingReport(token, selectedUser || undefined);
        setReportData(data);
      } catch (err: any) {
        console.error('Error loading aging report:', err);
        setError('Failed to load requirement aging report');
        setReportData(null);
      } finally {
        setLoading(false);
      }
    }
    loadReport();
  }, [token, selectedUser]);

  // Filter requirements by timescale
  const filteredRequirements = useMemo(() => {
    if (!reportData) return [];
    const maxDays = TIMESCALE_DAYS[timescale];
    if (maxDays === Infinity) return reportData.requirements;
    return reportData.requirements.filter(r => r.days_since_created <= maxDays);
  }, [reportData, timescale]);

  // Transform data into nivo scatterplot format grouped by assignee
  const chartData = useMemo(() => {
    if (filteredRequirements.length === 0) return [];
    // Track coordinate usage to jitter overlapping points
    const seen: Record<string, number> = {};
    const groups: Record<string, { x: number; y: number; req: reportService.AgingRequirement }[]> = {};
    for (const req of filteredRequirements) {
      const baseX = req.days_since_created;
      const baseY = req.story_points ?? 0;
      const key = `${baseX},${baseY}`;
      const count = seen[key] || 0;
      seen[key] = count + 1;
      // Spread duplicates in a small circle around the original point
      const angle = (count * 2.39996); // golden angle in radians for even spread
      const radius = count > 0 ? 0.3 : 0;
      const x = baseX + radius * Math.cos(angle);
      const y = baseY + radius * Math.sin(angle);

      const name = req.assigned_to_name || 'Unassigned';
      if (!groups[name]) groups[name] = [];
      groups[name].push({ x, y, req });
    }
    return Object.entries(groups).map(([name, data]) => ({
      id: name,
      data,
    }));
  }, [filteredRequirements]);

  // Summary stats
  const summary = useMemo(() => {
    if (filteredRequirements.length === 0) {
      return { total: 0, avgAge: 0, oldest: 0, unpointed: 0 };
    }
    const reqs = filteredRequirements;
    const totalAge = reqs.reduce((sum, r) => sum + r.days_since_created, 0);
    const oldest = Math.max(...reqs.map(r => r.days_since_created));
    const unpointed = reqs.filter(r => r.story_points == null || r.story_points === 0).length;
    return {
      total: reqs.length,
      avgAge: Math.round(totalAge / reqs.length),
      oldest,
      unpointed,
    };
  }, [filteredRequirements]);

  // Max values for axis
  const maxX = useMemo(() => {
    if (filteredRequirements.length === 0) return 10;
    return Math.max(...filteredRequirements.map(r => r.days_since_created)) + 1;
  }, [filteredRequirements]);

  const maxY = useMemo(() => {
    if (filteredRequirements.length === 0) return 5;
    return Math.max(...filteredRequirements.map(r => r.story_points ?? 0)) + 1;
  }, [filteredRequirements]);

  const getPriorityBadgeClass = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'medium':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'low':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-700 rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-2xl font-bold text-white">Requirement Aging Report</h1>
          <p className="text-purple-100 mt-1">Visualize approved requirement age vs story points</p>
        </div>

        {/* Filters */}
        {reportData && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
            <div className="flex flex-wrap gap-6">
              {/* Timescale */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Time Range
                </label>
                <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                  {([['week', 'Week'], ['month', 'Month'], ['quarter', 'Quarter'], ['year', 'Year'], ['all', 'All Time']] as const).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setTimescale(key)}
                      className={`px-4 py-2 text-sm font-medium transition-colors ${
                        timescale === key
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                      } border-r border-gray-300 dark:border-gray-600 last:border-r-0`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* User Filter (hidden for taskboard_user) */}
              {!isTaskBoardUser && reportData.users.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Filter by Assignee
                  </label>
                  <select
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    className="w-full md:w-72 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">All Users</option>
                    <option value="unassigned">Unassigned</option>
                    {reportData.users.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        )}

        {!loading && reportData && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Requirements</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{summary.total}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">Avg Age (days)</p>
                <p className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">{summary.avgAge}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">Oldest Item</p>
                <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">{summary.oldest}d</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">Unpointed</p>
                <p className="text-3xl font-bold text-red-600 dark:text-red-400">{summary.unpointed}</p>
              </div>
            </div>

            {/* Scatterplot */}
            {filteredRequirements.length > 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Age vs Story Points
                </h2>
                <div style={{ height: 450 }}>
                  <ResponsiveScatterPlot
                    data={chartData}
                    margin={{ top: 20, right: 140, bottom: 60, left: 70 }}
                    xScale={{ type: 'linear', min: 0, max: maxX }}
                    yScale={{ type: 'linear', min: -0.5, max: maxY }}
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 5,
                      legend: 'Days Since Creation',
                      legendPosition: 'middle' as const,
                      legendOffset: 46,
                    }}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 5,
                      legend: 'Story Points',
                      legendPosition: 'middle' as const,
                      legendOffset: -50,
                    }}
                    markers={[
                      {
                        axis: 'y',
                        value: 0,
                        lineStyle: { stroke: '#000000', strokeWidth: 2 },
                      },
                    ]}
                    colors={{ scheme: 'nivo' }}
                    nodeSize={10}
                    useMesh={true}
                    legends={[
                      {
                        anchor: 'bottom-right' as const,
                        direction: 'column' as const,
                        translateX: 130,
                        itemWidth: 100,
                        itemHeight: 20,
                        symbolSize: 10,
                        symbolShape: 'circle' as const,
                      },
                    ]}
                    tooltip={({ node }: any) => {
                      const req = node.data.req as reportService.AgingRequirement;
                      return (
                        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, boxShadow: '0 4px 6px rgba(0,0,0,0.1)', fontSize: 12, whiteSpace: 'nowrap' }}>
                          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>{req.title}</div>
                          <table style={{ borderCollapse: 'collapse' }}>
                            <tbody>
                              <tr><td style={{ color: '#6b7280', paddingRight: 12, paddingBottom: 2 }}>Priority</td><td style={{ fontWeight: 500, paddingBottom: 2, color: PRIORITY_COLORS[req.priority] || '#6b7280' }}>{req.priority}</td></tr>
                              <tr><td style={{ color: '#6b7280', paddingRight: 12, paddingBottom: 2 }}>Status</td><td style={{ fontWeight: 500, paddingBottom: 2 }}>{req.status}</td></tr>
                              <tr><td style={{ color: '#6b7280', paddingRight: 12, paddingBottom: 2 }}>Age</td><td style={{ fontWeight: 500, paddingBottom: 2 }}>{req.days_since_created} days</td></tr>
                              <tr><td style={{ color: '#6b7280', paddingRight: 12, paddingBottom: 2 }}>Points</td><td style={{ fontWeight: 500, paddingBottom: 2 }}>{req.story_points ?? 'None'}</td></tr>
                              <tr><td style={{ color: '#6b7280', paddingRight: 12, paddingBottom: 2 }}>Assignee</td><td style={{ fontWeight: 500, paddingBottom: 2 }}>{req.assigned_to_name || 'Unassigned'}</td></tr>
                              <tr><td style={{ color: '#6b7280', paddingRight: 12 }}>Project</td><td style={{ fontWeight: 500 }}>{req.project_name}</td></tr>
                            </tbody>
                          </table>
                        </div>
                      );
                    }}
                  />
                </div>
                {/* Legend note */}
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                  Requirements with no story points are plotted at Y=0.
                </p>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center mb-6">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No approved requirements found</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {selectedUser ? 'Try selecting a different user or clear the filter.' : 'Approve requirements to see them on this report.'}
                </p>
              </div>
            )}

            {/* Data Table */}
            {filteredRequirements.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Requirement Details ({filteredRequirements.length})
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Title</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Project</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Priority</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Points</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Age (days)</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Assignee</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredRequirements.map((req) => (
                        <tr key={req.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100 max-w-xs truncate">{req.title}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{req.project_name}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${getPriorityBadgeClass(req.priority)}`}>{req.priority}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{req.story_points ?? '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{req.days_since_created}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{req.assigned_to_name || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{req.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

export default RequirementAgingReport;
